import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Users, Trash2, Plus, Minus, AlertCircle } from "lucide-react";
import { Controller, Control, FieldErrors, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useGetMembershipsQuery } from "@/services/membershipsApi";
import { useGetCategoriesQuery } from "@/services/categoryApi";
import { MultiSelect } from "@/components/ui/multiselect/multiselectCheckbox";
import { toast } from "@/components/ui/use-toast";
import Combobox from "@/components/ui/common/combobox";
import { PriceMaster } from "@/types/system-setting/priceMaster";

interface PlanBasedPricingProps {
  control: Control<PriceMaster>;
  errors: FieldErrors<PriceMaster>;
  watch: UseFormWatch<PriceMaster>;
  setValue: UseFormSetValue<PriceMaster>;
  readOnly?: boolean;
}

const PlanBasedPricing = ({ control, errors, watch, setValue, readOnly = false }: PlanBasedPricingProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  
  const isEnabled = watch("enable_plan_pricing");
  const planPricing = watch("plan_pricing") || [];
  const regularPrice = watch("price") || 0;

  const { data: membershipPlans, isLoading: membershipLoading } = useGetMembershipsQuery(
    { query: `sort_key=id&sort_order=desc&status=active&type=default`, org_id: orgId },
    { skip: !orgId }
  );

  const { data: membershipGroupData, isLoading: membershipGroupLoading } = useGetCategoriesQuery(
    { query: "type=membership", org_id: orgId },
    { skip: !orgId }
  );

  useEffect(() => {
    if (planPricing.length > 0 && !isEnabled) {
      setValue("enable_plan_pricing", true);
    }
  }, [planPricing, isEnabled, setValue]);

  const membershipPlanOptions = useMemo(() => {
    return membershipPlans?.data.map((plan) => ({
      label: plan.name,
      value: String(plan.id),
      group_id: plan.group_id,
    }));
  }, [membershipPlans]);

  const membershipGroupOptions = useMemo(() => {
    return Array.isArray(membershipGroupData?.data)
      ? membershipGroupData.data.map((group) => ({
          label: group.name,
          value: String(group.id),
        }))
      : [];
  }, [membershipGroupData]);

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Users className="h-4 w-4 text-green-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Plan Based Pricing</h3>
            <p className="text-xs text-gray-600">Set different prices for different membership plans</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Enable</span>
          <Switch
            checked={isEnabled}
            onCheckedChange={(checked) => {
              setValue("enable_plan_pricing", checked);
              if (checked) {
                if (planPricing.length === 0) {
                  setValue("plan_pricing", [{ group_id: 0, membership_plan_ids: [], price: 0 }]);
                }
              } else {
                setValue("plan_pricing", []);
              }
            }}
            disabled={readOnly}
          />
        </div>
      </div>

      {isEnabled && (
        <div className="space-y-4 mt-4">
          <Controller
            control={control}
            name="plan_pricing"
            rules={{
              validate: {
                plansValid: (plans) => {
                  if (!plans || plans.length === 0) return true;
                  if (plans.length > 7) return "Maximum 7 plans allowed";
                  for (let i = 0; i < plans.length; i++) {
                    if (!plans[i].group_id) return `Plan ${i + 1}: Membership Group is required`;
                    if (!plans[i].membership_plan_ids || plans[i].membership_plan_ids.length === 0)
                      return `Plan ${i + 1}: At least one membership plan is required`;
                    if (plans[i].price <= 0) return `Plan ${i + 1}: Price should be greater than zero`;
                  }
                  const allMembershipIds = plans.flatMap((p) => p.membership_plan_ids);
                  if (allMembershipIds.length !== new Set(allMembershipIds).size)
                    return "Duplicate membership plans selected across different entries";
                  return true;
                },
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <div className="space-y-4">
                {error && <p className="text-red-500 text-sm">{error.message}</p>}
                {field.value?.map((plan, index) => (
                  <Card key={index} className="p-4 bg-white border-2 border-green-100 hover:border-green-200 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="outline" className="text-xs text-green-700 border-green-300">
                        Plan {index + 1}
                      </Badge>
                      {(field.value?.length || 0) > 1 && !readOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newPlans = [...(field.value || [])];
                            newPlans.splice(index, 1);
                            field.onChange(newPlans);
                          }}
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Membership Group <span className="text-red-500">*</span>
                        </label>
                        <Combobox
                          list={membershipGroupOptions}
                          defaultValue={membershipGroupOptions?.find((g) => g.value === String(plan.group_id))?.label || ""}
                          setFilter={(selectedLabel: string) => {
                            const selectedOption = membershipGroupOptions?.find((option) => option.label === selectedLabel);
                            const newPlans = [...(field.value || [])];
                            if (selectedOption) {
                              if (newPlans[index].group_id !== Number(selectedOption.value)) {
                                newPlans[index].group_id = Number(selectedOption.value);
                                newPlans[index].membership_plan_ids = [];
                              }
                            } else {
                              newPlans[index].group_id = 0;
                              newPlans[index].membership_plan_ids = [];
                            }
                            field.onChange(newPlans);
                          }}
                          label="Membership Group"
                          showLabel={false}
                          disabled={membershipGroupLoading || readOnly}
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Membership Plan <span className="text-red-500">*</span>
                        </label>
                        <MultiSelect
                          options={membershipPlanOptions?.filter((m) => m.group_id === plan.group_id).map((m) => ({
                            label: m.label,
                            value: Number(m.value),
                          })) || []}
                          value={plan.membership_plan_ids}
                          onChange={(selected: (string | number)[]) => {
                            const newPlans = [...(field.value || [])];
                            newPlans[index].membership_plan_ids = selected.map(Number);
                            field.onChange(newPlans);
                          }}
                          placeholder="Select Plans"
                          disabled={!plan.group_id || readOnly}
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Price <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <p className="absolute left-3 top-[45%] transform -translate-y-1/2 h-4 w-4 text-gray-500 font-medium">Rs.</p>
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            value={plan.price}
                            className="pl-10"
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              const newPlans = [...(field.value || [])];
                              newPlans[index].price = isNaN(value) ? 0 : value;
                              field.onChange(newPlans);
                            }}
                            placeholder="0.00"
                            disabled={readOnly}
                          />
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}

                {!readOnly && (
                  <div className="flex items-center justify-between pt-4 border-t border-green-200">
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400"
                        onClick={() => {
                          if ((field.value?.length || 0) < 7) {
                            field.onChange([...(field.value || []), { group_id: 0, membership_ids: [], price: 0 }]);
                          } else {
                            toast({ variant: "destructive", description: "Maximum 7 plans allowed" });
                          }
                        }}
                        disabled={membershipLoading || (field.value?.length || 0) >= 7}
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Plan
                      </Button>
                      <div className="text-xs text-gray-500">{field.value?.length || 0}/7 rules</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default PlanBasedPricing;
